import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  Badge,
  Text,
  Card,
  Collapsible,
  Layout,
  SkeletonBodyText,
  InlineStack,
  BlockStack,
  Box,
  Button,
} from "@shopify/polaris";
import { useQuery } from "@apollo/client";

// context
import { PrivateContext } from "lib/context";

import { baseHelper, errorHelper } from "lib/helpers";
import constant from "lib/constant/constant";
import { Banner, MultiCarousel, ReportCardSkeleton } from "lib/components";

import { GET_ALL_COUNTS, GET_LAKE_COUNTS } from "app/reports/apollo/queries";
import { ORDER_DATA, PRODUCT_DATA, SHOPIFY_PRODUCT_DATA } from "app/reports/apollo/subscriptions";

import ReportCard from "app/reports/generic/style/reportCard";
import { useLocation } from "react-router-dom";
import config from "./quickReportConfig";

const {
  gql: { GET_COUNT, GET_LAKE_COUNT, ORDER_COUNT, PRODUCT_COUNT, SHOPIFY_PRODUCT_COUNT },
  DOWN,
  UP,
} = constant;

const ProviderQuickReport = () => {
  const { cms, currentUser, vendorHiddenModule = [] } = useContext(PrivateContext);
  const { ecommercePlatform } = currentUser;
  const location = useLocation();
  const path = location.pathname;
  const { data: dashboardData, loading: dashboardLoading, error: dashboardError, subscribeToMore } = useQuery(
    [constant.WOOCOMMERCE, constant.BIGCOMMERCE].includes(ecommercePlatform) ? GET_LAKE_COUNTS : GET_ALL_COUNTS
  );

  const [openQuickReport, setQuickReport] = useState(false);
  const [banner, setBanner] = useState({
    isOpen: false,
    status: "",
    title: "",
  });
  const [count, setCount] = useState({
    orderCount: {},
    paymentCount: {},
    productCount: {},
    wooTempProductCount: {},
    wixTempProductCount: {},
    shopifyProductCount: {},
    prestaShopTempProductCount: {},
    magentoTempProductCount: {},
  });
  useEffect(() => {
    if (dashboardError) {
      setBanner({
        isOpen: true,
        status: constant.CRITICAL,
        title: errorHelper.parse(dashboardError),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardError, errorHelper]);
  useEffect(() => {
    const responseData = baseHelper.getResponseData(
      dashboardData,
      [constant.WOOCOMMERCE, constant.BIGCOMMERCE].includes(ecommercePlatform) ? GET_LAKE_COUNT : GET_COUNT
    );
    const responseError = baseHelper.getResponseError(
      dashboardData,
      [constant.WOOCOMMERCE, constant.BIGCOMMERCE].includes(ecommercePlatform) ? GET_LAKE_COUNT : GET_COUNT
    );
    if (responseError) {
      setBanner({
        isOpen: true,
        status: constant.CRITICAL,
        title: responseError,
      });
      return;
    }
    if (!responseData) {
      return;
    }
    const {
      orderCount = null,
      paymentCount = null,
      productCount = null,
      prestaShopTempProductCount = null,
      magentoTempProductCount = null,
      wooTempProductCount = null,
      wixTempProductCount = null,
      shopifyProductCount = null,
    } = responseData;
    setCount({
      orderCount: orderCount || {},
      paymentCount: paymentCount || {},
      productCount: productCount || {},
      prestaShopTempProductCount: prestaShopTempProductCount || {},
      magentoTempProductCount: magentoTempProductCount || {},
      wooTempProductCount: wooTempProductCount || {},
      wixTempProductCount: wixTempProductCount || {},
      shopifyProductCount: shopifyProductCount || {},
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardData]);

  const createSubscribeToMore = useCallback(
    ({ document, responseKey }) => {
      subscribeToMore({
        document,
        updateQuery: (prev, { subscriptionData }) => {
          if (!subscriptionData.data) {
            return prev;
          }
          const responseData = baseHelper.getResponseData(subscriptionData.data, responseKey);
          if (!responseData) {
            const responseError = baseHelper.getResponseError(subscriptionData.data, responseKey);
            setBanner({
              isOpen: true,
              status: constant.CRITICAL,
              title: responseError,
            });
            return null;
          }
          setCount((prevState) => ({
            ...prevState,
            [responseKey]: responseData[responseKey] || {},
          }));
          return true;
        },
      });
    },
    [subscribeToMore]
  );

  useEffect(() => {
    switch (path) {
      case "/":
        createSubscribeToMore({ document: ORDER_DATA, responseKey: ORDER_COUNT });
        createSubscribeToMore({ document: PRODUCT_DATA, responseKey: PRODUCT_COUNT });
        break;
      case "/orders":
        createSubscribeToMore({ document: ORDER_DATA, responseKey: ORDER_COUNT });
        break;
      case "/products":
        createSubscribeToMore({ document: PRODUCT_DATA, responseKey: PRODUCT_COUNT });
        break;
      case "/products/shopify":
        createSubscribeToMore({ document: SHOPIFY_PRODUCT_DATA, responseKey: SHOPIFY_PRODUCT_COUNT });
        break;
      default:
        break;
    }
  }, [createSubscribeToMore, path]);

  // data taken from report config
  const quickReportData = config({ cms, count, path });

  const isHome = path === "/";
  const onCloseBanner = () => {
    setBanner({
      isOpen: false,
      status: "",
      title: "",
    });
  };

  // const noDataAvailable = (
  //   <Card.Section subdued key="noData">
  //     <InlineStack>
  //       <Text as="span">No data available</Text>
  //     </InlineStack>
  //   </Card.Section>
  // );

  const getQuickReportList = () => {
    if (!quickReportData) {
      return null;
    }
    const loading = <SkeletonBodyText lines={3} />;
    const dataStack = (quickReport) =>
      (isHome && (
        <InlineStack align="space-between" blockAlign="center">
          <BlockStack>
            <Text as="span" variant="bodyLg" fontWeight="medium">
              {quickReport.label}
            </Text>
            <Text variant="bodySm" as="p" fontWeight="regular">
              {quickReport.caption}
            </Text>
          </BlockStack>
          <Badge tone={constant.SUCCESS} status={constant.SUCCESS}>
            {quickReport.count}
          </Badge>
        </InlineStack>
      )) || (
        <Card>
          <BlockStack>
            <Text as="h4" variant="headingXl" fontWeight="semibold">
              {quickReport.count}
            </Text>
            <span className="card-title">{quickReport.label}</span>
          </BlockStack>
        </Card>
      );
    const reportData = quickReportData.map((quickReport, index) => {
      const { count: reportCount = null, label = "" } = quickReport;
      if (!reportCount && reportCount !== 0) {
        return null;
      }
      if (!vendorHiddenModule.includes(label)) {
        return (
          <>
            {isHome && index > 0 && index !== quickReportData.length && <br />}
            {dashboardLoading ? loading : dataStack(quickReport)}
          </>
        );
      }
      return null;
    });

    const filteredReportData = reportData.filter((data) => data);

    if (filteredReportData.length) {
      // return filteredReportData;
      return (
        (isHome && filteredReportData) || (
          <ReportCard>
            <MultiCarousel containerClass="quick-reports" slideItems={filteredReportData} />
          </ReportCard>
        )
      );
    }
    return null;
  };

  const home = () => {
    return (
      (isHome && dashboardLoading && (
        <>
          <br />
          <SkeletonBodyText lines={3} />
        </>
      )) || (
        <Card>
          <Box>
            <InlineStack align="space-between">
              <Text as="h6" variant="headingMd">
                {cms("common.label.quickReport")}
              </Text>
              <Button variant="plain" onClick={() => setQuickReport(!openQuickReport)} disclosure>
                {openQuickReport ? cms("common.label.hide") : cms("common.label.show")}
              </Button>
            </InlineStack>
          </Box>

          <Collapsible open={openQuickReport} id="collapsibleQuickReport">
            <br />
            {getQuickReportList()}
          </Collapsible>
        </Card>
      )
    );
  };

  return (
    <>
      {banner.isOpen && (
        <>
          <Banner isOpen={banner.isOpen} status={banner.status} title={banner.title} onDismiss={onCloseBanner} />
          <br />
        </>
      )}
      {/* {home()} */}
      {/* {getQuickReportList()} */}
      {(isHome && home()) ||
        (dashboardLoading && <ReportCardSkeleton cardCount={quickReportData.length} />) ||
        getQuickReportList()}
    </>
  );
};

export default ProviderQuickReport;
